import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Users } from 'lucide-react'
import type { CreatorPoolItem } from '@/api/client'
import { criteriaSummary, fmtCount, useCreatorPools } from '@/lib/creators'
import { formatShort } from '@/lib/time'
import { Button, EmptyState, ErrorState, PageHeader, PlatformMark, Skeleton, StatusPill, useToast, useWidePage } from '@/ui'
import { CreatorsTabs } from './CreatorsTabs'
import { PoolForm } from './PoolForm'

function runText(p: CreatorPoolItem): string {
  const r = p.last_run
  if (!r) return '아직 찾은 적 없다'
  if (r.status === 'running') return '찾는 중'
  const when = formatShort(r.finished_at ?? r.started_at)
  return r.status === 'failed' ? `${when} 실패` : `${when} · ${r.added}명 추가`
}

function PoolCard({ p }: { p: CreatorPoolItem }) {
  return (
    <Link
      to={`/creators/${p.id}`}
      className="flex min-w-0 flex-1 flex-col gap-4 rounded-lg border border-border bg-card p-4 transition-colors hover:border-foreground/20"
    >
      <div className="min-w-0 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <h2 className="flex min-w-0 items-center gap-2 text-section font-semibold">
            <PlatformMark platform={p.platform} />
            <span className="truncate">{p.name}</span>
          </h2>
          <StatusPill domain="app" status={p.status} />
        </div>
        <p className="text-table text-muted-foreground">{criteriaSummary(p)}</p>
      </div>
      <div className="grid grid-cols-2 gap-3 border-t border-border pt-3">
        <div className="min-w-0">
          <div className="text-meta text-muted-foreground">풀에 있는 사람</div>
          <div className="mt-1 text-title font-semibold tabular-nums">{fmtCount(p.members_in)}명</div>
        </div>
        <div className="min-w-0">
          <div className="text-meta text-muted-foreground">마지막으로 찾음</div>
          <div className="mt-1 truncate text-body">{runText(p)}</div>
          {p.discovery_enabled && <div className="mt-0.5 text-micro text-muted-foreground">매일 자동</div>}
        </div>
      </div>
    </Link>
  )
}

/** /creators: the target pools. A pool says who to reach; what to send them
 *  is a later step. */
export default function PoolList() {
  useWidePage()
  const q = useCreatorPools()
  const navigate = useNavigate()
  const toast = useToast()
  const [creating, setCreating] = useState(false)
  const items = q.data?.items ?? []

  const newButton = (
    <Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
      새 풀
    </Button>
  )

  return (
    <>
      <PageHeader
        title="타깃 풀"
        docTitle="인플루언서 · 타깃 풀"
        description="협업을 제안할 만한 사람을 조건으로 찾아 모아 둔다. 무엇을 보낼지는 풀을 만든 다음에 정한다."
        actions={items.length > 0 ? newButton : undefined}
      >
        <CreatorsTabs />
      </PageHeader>
      {q.error && !q.data ? (
        <ErrorState title="풀을 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
      ) : q.isPending ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Users}
          title="풀이 없다"
          description="풀은 분야와 팔로워 같은 조건에 맞는 사람을 모아 둔 목록이다. 조건을 정하면 사람을 찾아 채운다."
          actions={newButton}
        />
      ) : (
        <div role="list" aria-label="풀 목록" className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {items.map((p) => (
            <div key={p.id} role="listitem" className="flex min-w-0">
              <PoolCard p={p} />
            </div>
          ))}
        </div>
      )}
      {creating && (
        <PoolForm
          onClose={() => setCreating(false)}
          onSaved={(p) => {
            toast.success(`${p.name} 풀을 만들었다`)
            setCreating(false)
            navigate(`/creators/${p.id}`)
          }}
        />
      )}
    </>
  )
}
